import { queryOptions, useQuery } from "@tanstack/react-query";
import { chartOfAccountsTemplateApplyEventContract } from "@common/contracts/chart-of-accounts-template-apply-event.contract";
import type { CoaTemplateApplyHistoryEventRef } from "@common/types/auditLog";
import {
    ChartOfAccountsTemplateApplyEventDetail,
    CoaApplyUndoEligibility,
    CoaApplyUndoResponse,
    chartOfAccountsTemplateApplyEventDetailSchema,
    coaApplyUndoEligibilitySchema,
    coaApplyUndoResponseSchema,
} from "@common/schemas/domains/accounting/chart-of-accounts-template-apply-event.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useScope } from "@state/scopeContext.lib";

const client = getBackendAPIClient(chartOfAccountsTemplateApplyEventContract);

export interface UndoCoaTemplateApplyEventParams {
    workspaceId: string;
    eventId: string;
    undoInvocationId: string;
}

export const coaTemplateApplyEventQueryKeys = {
    all: (): readonly [string] => ["coa-template-apply-event"],
    // The multi-entity Activity drawer loads every event behind one aggregate
    // query. Rooting it under the same prefix as detail/eligibility means a
    // single `invalidateQueries({ queryKey: all() })` refreshes the aggregate and
    // all per-event entries together.
    activity: (
        eventRefs: readonly CoaTemplateApplyHistoryEventRef[],
    ): readonly [string, string, Array<[string, string, number]>] => [
        "coa-template-apply-event",
        "activity",
        eventRefs.map((eventRef) => [
            eventRef.workspaceId,
            eventRef.eventId,
            eventRef.entityId,
        ]),
    ],
    detail: (
        workspaceId: string,
        eventId: string,
    ): readonly [string, string, string, string] => [
        "coa-template-apply-event",
        "detail",
        workspaceId,
        eventId,
    ],
    eligibility: (
        workspaceId: string,
        eventId: string,
    ): readonly [string, string, string, string] => [
        "coa-template-apply-event",
        "eligibility",
        workspaceId,
        eventId,
    ],
};

export async function fetchCoaTemplateApplyEventDetail({
    workspaceId,
    eventId,
}: {
    workspaceId: string;
    eventId: string;
}): Promise<ChartOfAccountsTemplateApplyEventDetail> {
    const response = await client.getApplyEvent({
        params: { workspaceId, eventId },
    });
    return chartOfAccountsTemplateApplyEventDetailSchema.parse(response.body);
}

export async function fetchCoaTemplateApplyEventEligibility({
    workspaceId,
    eventId,
}: {
    workspaceId: string;
    eventId: string;
}): Promise<CoaApplyUndoEligibility> {
    const response = await client.getUndoEligibility({
        params: { workspaceId, eventId },
    });
    return coaApplyUndoEligibilitySchema.parse(response.body);
}

export async function undoCoaTemplateApplyEvent({
    workspaceId,
    eventId,
    undoInvocationId,
}: UndoCoaTemplateApplyEventParams): Promise<CoaApplyUndoResponse> {
    const response = await client.undoApplyEvent({
        params: { workspaceId, eventId },
        body: { undoInvocationId },
        extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
    });
    return coaApplyUndoResponseSchema.parse(response.body);
}

// An apply event and its undo eligibility are append-only audit records, so a
// short stale window lets the multi-entity Activity aggregate and any single
// per-event read share one cache entry instead of refetching per consumer.
const APPLY_EVENT_STALE_TIME_MS = 60_000;

export function coaTemplateApplyEventDetailQueryOptions({
    workspaceId,
    eventId,
}: {
    workspaceId: string;
    eventId: string;
}) {
    return queryOptions({
        queryKey: coaTemplateApplyEventQueryKeys.detail(workspaceId, eventId),
        queryFn: () =>
            fetchCoaTemplateApplyEventDetail({ workspaceId, eventId }),
        staleTime: APPLY_EVENT_STALE_TIME_MS,
    });
}

export function coaTemplateApplyEventEligibilityQueryOptions({
    workspaceId,
    eventId,
}: {
    workspaceId: string;
    eventId: string;
}) {
    return queryOptions({
        queryKey: coaTemplateApplyEventQueryKeys.eligibility(
            workspaceId,
            eventId,
        ),
        queryFn: () =>
            fetchCoaTemplateApplyEventEligibility({ workspaceId, eventId }),
        staleTime: APPLY_EVENT_STALE_TIME_MS,
    });
}

export function useCoaTemplateApplyEventDetailQuery({
    workspaceId: requestedWorkspaceId,
    eventId,
    enabled = true,
}: {
    workspaceId?: string | null;
    eventId: string | null;
    enabled?: boolean;
}) {
    const { workspaceId: scopedWorkspaceId } = useScope();
    const workspaceId = requestedWorkspaceId ?? scopedWorkspaceId;

    return useQuery({
        ...coaTemplateApplyEventDetailQueryOptions({
            workspaceId: workspaceId ?? "",
            eventId: eventId ?? "",
        }),
        queryFn: async (): Promise<ChartOfAccountsTemplateApplyEventDetail> => {
            if (!workspaceId || !eventId) {
                throw new Error("Missing workspace or event id");
            }
            return await fetchCoaTemplateApplyEventDetail({
                workspaceId,
                eventId,
            });
        },
        enabled: !!workspaceId && !!eventId && enabled,
    });
}
